import { useI18n } from "@/i18n/I18nProvider";
import OrnamentDivider from "@/components/OrnamentDivider";

const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d6f58c33-0a17-4479-ae12-e5dc7e28c8d7/images/338673aaa882d30e0c4adf08d441507dc253d6bc5a83005fed419049f2c2ca8f.png";

export default function About() {
  const { t } = useI18n();

  return (
    <section
      id="story"
      data-testid="about-section"
      className="relative py-28 sm:py-36 bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] kh-section-cinematic"
    >
      {/* Burgundy ambient backwash */}
      <div className="absolute top-1/3 right-0 w-1/2 h-96 pointer-events-none opacity-25"
        style={{ background: "radial-gradient(ellipse at right center, rgba(169, 48, 67, 0.32), transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image with hand-crafted frame */}
          <div className="md:col-span-5 relative">
            <div className="kh-belt-frame aspect-[4/5] overflow-hidden border border-[#1f1f29] relative">
              <img
                src={ABOUT_IMG}
                alt="KirchHero — premium fabric with gold detail"
                className="w-full h-full object-cover grayscale-[0.4] hover:grayscale-0 transition-all duration-[1500ms]"
              />
              {/* Gold corner stamps */}
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black via-black/60 to-transparent" />
              {/* Stamp label */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between border-t border-[#D4AF37]/40 pt-3">
                <span className="font-serif-luxury italic text-xs text-[#D4AF37]">
                  Crafted in Spirit
                </span>
                <span className="font-display text-xs tracking-[0.3em] text-white/60">
                  KH · 01
                </span>
              </div>
            </div>
            {/* Royal-blue offset accent square */}
            <div className="hidden md:block absolute -bottom-8 -right-8 w-40 h-40 border border-[#A93043] z-[-1]" />
            <div className="hidden md:block absolute -top-6 -left-6 w-20 h-20 border border-[#D4AF37]/60 z-[-1]" />
          </div>

          {/* Text */}
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#A93043]" />
              <span className="kh-chapter">{t.about.chapter}</span>
              <span className="h-px w-8 bg-[#A93043]" />
            </div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="kh-eyebrow">{t.about.eyebrow}</span>
            </div>

            {/* Title with serif accent */}
            <h2
              data-testid="about-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.92]"
            >
              {t.about.title.split("—")[0]}
              <span className="font-serif-luxury italic text-[#D4AF37] normal-case tracking-normal">
                — {t.about.title.split("—")[1]?.trim()}
              </span>
            </h2>

            {/* Lead paragraph with luxury feel */}
            <p className="mt-8 font-serif-luxury text-xl sm:text-2xl text-white/85 leading-snug italic max-w-xl">
              "{t.about.paragraph1}"
            </p>
            <p className="mt-6 font-body text-base text-white/60 leading-relaxed max-w-xl">
              {t.about.paragraph2}
            </p>

            {/* Stats — champion-belt style */}
            <div
              data-testid="about-stats"
              className="mt-12 grid grid-cols-3 gap-6 border-t border-[#1f1f29] pt-8"
            >
              {[
                { v: t.about.stat1Value, l: t.about.stat1Label, accent: "gold" },
                { v: t.about.stat2Value, l: t.about.stat2Label, accent: "copper" },
                { v: t.about.stat3Value, l: t.about.stat3Label, accent: "gold" },
              ].map((s, i) => (
                <div key={i} className="relative">
                  <div className={`font-display text-4xl sm:text-5xl leading-none ${s.accent === "copper" ? "text-[#B5651D]" : "text-[#D4AF37]"}`}>
                    {s.v}
                  </div>
                  <div className="mt-2 font-body text-xs tracking-[0.2em] uppercase text-white/50">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 mt-20">
        <OrnamentDivider />
      </div>
    </section>
  );
}
